// CONTRACT:C4-DESIGN-TOKENS.1.0
// COP — Cartouche (Item 8, §2.8). React shell only; never enters the render loop.
//
// A tiny CALM corner readout docked at the bottom-right of the map: it names the
// EDITION of reality + the connectivity/fidelity tier the operator holds, plus a
// live coord readout with a lat-lon ↔ MGRS toggle.
//
// Map-chrome family — NOT a heavy panel, so it claims NO heavy slot and registers
// NOTHING with the escape stack. Connectivity is a *named* state, never a gap:
// "LIVE / full / connected" is the calm rest face; a degraded/lost link is shown
// as its own NAMED tier (operators trust a named lost state). Honest-picture: the
// Edition Contract carries DEGRADATION; it never carries DENIAL (that is the
// Shroud's job), so nothing here ever reads as "Access Denied".
//
// Motion is the urgency channel and danger-only — this surface NEVER moves.

import { useState } from "react";
import type { CartoucheState, CursorReadout } from "../host/shellContract.ts";
import "./cartouche.css";

/** Classify a free-form link tier into a calm severity for SUBTLE tone only.
 *  This is honest degradation framing (named state), never a denial signal and
 *  never a motion trigger. Unknown strings fall back to the calm "nominal" tone. */
function linkSeverity(linkTier: string): "nominal" | "degraded" | "lost" {
  const t = linkTier.toLowerCase();
  if (/(lost|offline|down|dark|no\s*link|disconnect)/.test(t)) return "lost";
  if (/(degrad|partial|intermittent|weak|limited|ddil|stale|slow)/.test(t)) {
    return "degraded";
  }
  return "nominal";
}

/** Format a signed decimal degree with a hemisphere letter — the field-legible
 *  coord vernacular (no minus signs to misread under load). */
function fmtDeg(value: number, axis: "lat" | "lon"): string {
  const hemi = axis === "lat" ? (value >= 0 ? "N" : "S") : value >= 0 ? "E" : "W";
  return `${Math.abs(value).toFixed(5)}°${hemi}`;
}

export function Cartouche({
  edition,
  cursor,
}: {
  edition: CartoucheState;
  cursor: CursorReadout | null;
}): JSX.Element {
  // Persistent local readout mode — a calm in-place toggle, not a summoned panel.
  const [showMgrs, setShowMgrs] = useState(true);
  const severity = linkSeverity(edition.linkTier);

  const coord = cursor
    ? showMgrs
      ? cursor.mgrs
      : `${fmtDeg(cursor.lat, "lat")} ${fmtDeg(cursor.lon, "lon")}`
    : "— off map —";

  return (
    <aside
      className="cartouche"
      data-link={severity}
      aria-label="Edition and coordinate readout"
    >
      {/* EDITION CONTRACT — named edition of reality + fidelity + link tier. */}
      <div className="cartouche__edition" title="Edition of reality you hold">
        <span className="cartouche__edition-name">{edition.name}</span>
        <span className="cartouche__sep" aria-hidden="true">·</span>
        <span className="cartouche__fidelity">{edition.fidelity}</span>
      </div>

      <div className="cartouche__link" title="Connectivity tier (named state)">
        <span className="cartouche__link-dot" aria-hidden="true" />
        <span className="cartouche__link-tier">{edition.linkTier}</span>
      </div>

      {/* COORD READOUT — live cursor, lat-lon ↔ MGRS toggle (persistent). */}
      <button
        type="button"
        className="cartouche__coord"
        onClick={() => setShowMgrs((m) => !m)}
        aria-label={`Coordinate readout (${showMgrs ? "MGRS" : "lat-lon"}); click to toggle`}
        title="Toggle lat-lon ↔ MGRS"
      >
        <span className="cartouche__coord-mode" aria-hidden="true">
          {showMgrs ? "MGRS" : "LL"}
        </span>
        <span className="cartouche__coord-value" data-off={cursor ? undefined : ""}>
          {coord}
        </span>
      </button>
    </aside>
  );
}
